import { translate as uiText } from '@/shared/i18n';
/** Only CMS-registered mount assets are eligible. Host scripts are never replayed. */
export function createLearnerAssetLoader() {
    const loaded = new Map<string, Promise<void>>();
    document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src],link[rel=stylesheet][href]').forEach((el) => {
        loaded.set(el instanceof HTMLScriptElement ? el.src : el.href, Promise.resolve());
    });
    const load = (source: HTMLScriptElement | HTMLLinkElement, base: string): Promise<void> => {
        const script = source instanceof HTMLScriptElement;
        const url = new URL(source.getAttribute(script ? 'src' : 'href')!, base).href;
        if (loaded.has(url)) return loaded.get(url)!;
        // A deployment changed a fixed entry: don't mix two versions of its React contexts.
        if ([...loaded.keys()].some((prior) => new URL(prior).pathname === new URL(url).pathname)) {
            return Promise.reject(new Error(uiText("vidyalms.ui.f4722d0bc5e030fa", "The learning application was updated.")));
        }
        const promise = new Promise<void>((resolve, reject) => {
            const element = script ? document.createElement('script') : document.createElement('link');
            if (element instanceof HTMLScriptElement) {
                element.src = url;
                element.type = source.getAttribute('type') || 'text/javascript';
                element.async = false;
            } else {
                element.rel = 'stylesheet';
                element.href = url;
            }
            for (const attribute of ['integrity', 'crossorigin', 'referrerpolicy', 'media']) {
                if (source.hasAttribute(attribute)) element.setAttribute(attribute, source.getAttribute(attribute)!);
            }
            const nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
            if (nonce) element.nonce = nonce;
            element.dataset.vySpaAsset = 'true';
            const timer = window.setTimeout(() => fail(), 15000);
            const fail = () => {
                window.clearTimeout(timer);
                element.remove();
                loaded.delete(url);
                reject(new Error('A learning page asset could not be loaded.'));
            };
            element.addEventListener('error', fail, { once: true });
            element.addEventListener('load', () => {
                window.clearTimeout(timer);
                resolve();
            }, { once: true });
            document.head.append(element);
        });
        loaded.set(url, promise);
        return promise;
    };
    return {
        styles: (page: Document, base: string) => Promise.all(
            [...page.querySelectorAll<HTMLLinkElement>('link[data-vy-spa-asset][rel=stylesheet][href]')].map((el) => load(el, base)),
        ),
        scripts: async (page: Document, base: string) => {
            // Free app first, then renderer extensions in the CMS dependency order.
            for (const el of page.querySelectorAll<HTMLScriptElement>('script[data-vy-spa-asset][src]')) await load(el, base);
        },
    };
}
